import React from 'react';
import {Box} from '@mui/material';
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {t} from '../themes/statamicTokens';

const getPostsPerDay = (posts) => {
    if (!posts) return [];
    return Object.keys(posts)
        .sort()
        .map((dateKey) => ({date: dateKey, total: posts[dateKey]}));
};

const formatDate = (date) => {
    try {
        const d = new Date(date);
        return d.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
    } catch {
        return date;
    }
};

const ChartTooltip = ({active, payload, label}) => {
    if (!active || !payload || payload.length === 0) return null;
    const value = payload[0]?.value ?? 0;
    return (
        <Box
            sx={{
                backgroundColor: t.color.surface,
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.sm}px`,
                boxShadow: t.shadow.dropdown,
                px: 1.25,
                py: 0.75,
            }}
        >
            <Box sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                {formatDate(label)}
            </Box>
            <Box
                sx={{
                    fontSize: `${t.type.bodyStrong.size}px`,
                    fontWeight: 600,
                    color: t.color.text.primary,
                }}
            >
                {value} {value === 1 ? 'post' : 'posts'}
            </Box>
        </Box>
    );
};

const PostChart = ({posts}) => {
    const data = getPostsPerDay(posts);
    if (data.length === 0) return null;

    return (
        <Box sx={{width: '100%', height: 260}}>
            <ResponsiveContainer>
                <AreaChart
                    data={data}
                    margin={{top: 8, right: 12, left: 0, bottom: 0}}
                >
                    <defs>
                        <linearGradient id="postChartGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%"   stopColor={t.color.accent.primary} stopOpacity={0.25}/>
                            <stop offset="100%" stopColor={t.color.accent.primary} stopOpacity={0}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={t.color.border.hairline}
                        vertical={false}
                    />
                    <XAxis
                        dataKey="date"
                        tickFormatter={formatDate}
                        tick={{fill: t.color.text.muted, fontSize: 11}}
                        axisLine={{stroke: t.color.border.default}}
                        tickLine={false}
                    />
                    <YAxis
                        allowDecimals={false}
                        tick={{fill: t.color.text.muted, fontSize: 11}}
                        axisLine={false}
                        tickLine={false}
                        width={32}
                    />
                    <Tooltip content={<ChartTooltip/>} cursor={{stroke: t.color.border.strong}}/>
                    <Area
                        type="monotone"
                        dataKey="total"
                        stroke={t.color.accent.primary}
                        strokeWidth={2}
                        fill="url(#postChartGradient)"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default PostChart;
